/**
 * Is the hand-built plan's side panel showing figures for the inputs on screen, or still working them out?
 *
 * WHY (owner-seen, 25 Oct 2026). The panel's figures come from several calculations that run one after another —
 * the position, then the fees struck on it, then the projections struck on those, and the comparison beside them.
 * Each lands when it lands, so for 10–25 seconds after a change the panel mixed the new figures with the old ones:
 * "Customise this plan" first said the estate was unchanged and the savings lasted ten years, then settled on
 * +$122,016 and five; a blank Support at Home plan first read "+$748,771 vs today" before its real comparison
 * arrived. A figure that is about to change is worse than no figure, so while anything is in flight the panel says
 * "Working it out…" instead.
 *
 * HOW. Every calculation the panel reads is passed through `track`. The panel is working while any of them is in
 * flight, and for a short settle after the last one lands — because the next step of the chain (fees after the
 * position, projections after the fees) only starts once React has rendered the previous result, and a gap of a
 * frame or two must not flash the half-finished figures.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** How long the count must stay at nothing before the figures are called current. */
export const CALC_SETTLE_MS = 300;

export interface CalcTracker {
    /** Counts the promise in until it settles, either way. Returns the same promise. */
    track<T>(promise: Promise<T>): Promise<T>;
    /** In flight right now (for tests). */
    inFlight(): number;
    /** Stops any pending settle timer. */
    dispose(): void;
    /** Re-arms the settle when nothing is in flight (a remount after `dispose`). */
    kick(): void;
}

interface TrackerDeps {
    onWorking: (working: boolean) => void;
    settleMs?: number;
    setTimer?: (fn: () => void, ms: number) => unknown;
    clearTimer?: (handle: unknown) => void;
}

/**
 * The counting itself, free of React so it can be tested with fake timers. Starts WORKING, and settles after
 * `settleMs` if nothing is ever tracked (a screen with nothing to calculate is current).
 */
export function createCalcTracker({ onWorking, settleMs = CALC_SETTLE_MS, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (h) => clearTimeout(h as ReturnType<typeof setTimeout>) }: TrackerDeps): CalcTracker {
    let count = 0;
    let timer: unknown = null;
    const cancelSettle = () => {
        if (timer != null) clearTimer(timer);
        timer = null;
    };
    const scheduleSettle = () => {
        cancelSettle();
        timer = setTimer(() => {
            timer = null;
            if (count === 0) onWorking(false);
        }, settleMs);
    };
    scheduleSettle();
    return {
        track<T>(promise: Promise<T>): Promise<T> {
            count += 1;
            cancelSettle();
            onWorking(true);
            const done = () => {
                count = Math.max(0, count - 1);
                if (count === 0) scheduleSettle();
            };
            promise.then(done, done);
            return promise;
        },
        inFlight: () => count,
        dispose: cancelSettle,
        kick: () => { if (count === 0) scheduleSettle(); },
    };
}

/** The hook: `working` is true from mount until every tracked calculation has landed and settled. */
export function useCalcTracker(settleMs = CALC_SETTLE_MS): { track: <T>(promise: Promise<T>) => Promise<T>; working: boolean } {
    const [working, setWorking] = useState(true);
    const ref = useRef<CalcTracker | null>(null);
    if (!ref.current) ref.current = createCalcTracker({ onWorking: setWorking, settleMs });
    useEffect(() => {
        ref.current?.kick();
        return () => ref.current?.dispose();
    }, []);
    const track = useCallback(<T,>(promise: Promise<T>) => ref.current!.track(promise), []);
    return { track, working };
}
